// define a class that will contain all the code 
// required to add an additional XML / JSON node to 

import React, { ReactElement, ReactNode } from "react";
import { addComponent } from '../../../constants/jsonComponents';
import { addComponentType } from '../../../jsonParsing/flowchartToGraph';
import { JE_JsonValidationContext } from '../../../jsonParsing/interfaces';
import { registerNodeFQSchema, FQSchemaData } from '../../../jsonParsing/fqSpecGeneration';
import { BuildSettings } from '../../../jsonParsing/utils';


export interface AdditionalComponentProps {
  data: any;
  closeGritComponent: any;
  setUnsavedChanges: any;
}
// the editor 
abstract class AdditionalNode {
  abstract componentType: string;
  abstract getDndComponent(): ReactNode
  abstract getJSONCompDef(): any
  abstract initNodeJSON(): any
  abstract EditNodeUI(props: AdditionalComponentProps): ReactElement<any, any>;
  abstract validateJSON(
    nodeData: any,
    vCtx: JE_JsonValidationContext,
    addErrorReport: (message: string) => void,
    validateClaim: ValidateClaimFn
  ): void;
  abstract generateFQSchema(data: FQSchemaData): void;
  abstract getOutputClaims(
    nodeData: any,
    validationContext: JE_JsonValidationContext
  ): string[];
  abstract extractFCNData(
    nodeData: any,
    componentType: string,
    buildSettings: BuildSettings,
    linkType?: string
  ): any
}


// this will track all the nodes that are added
const additionalNodes: AdditionalNode[] = []

const findNodeByType = (nodeType: string) => additionalNodes.find(node => node.componentType === nodeType);

export function registerNode(node: AdditionalNode) {
  addComponentType(node.componentType);
  addComponent(node.componentType, node.getJSONCompDef());
  additionalNodes.push(node);
  registerNodeFQSchema(node.componentType, (data: FQSchemaData) => node.generateFQSchema(data));
}

export function extractFCNData(
  nodeData: any,
  componentType: string,
  buildSettings: BuildSettings,
  linkType?: string
) {
  const component = findNodeByType(componentType);
  if (component) {
    return component.extractFCNData(
      nodeData,
      componentType,
      buildSettings,
      linkType
    );
  } else {
    return {};
  }
}

export function init_AdditionalNode(type: string) {
  const node = findNodeByType(type);
  if (node) {
    return node.initNodeJSON()
  }
  return {};
}

export function getDndComponents() {
  return additionalNodes.map(node => node.getDndComponent());
}

export function getEditNodeComponentsMap() {
  const map: { [type: string]: any } = {};
  additionalNodes.forEach((node: AdditionalNode) => {
    map[node.componentType] = node.EditNodeUI
  });
  return map;
}

export function getAdditionalNodeOutputClaims(
  componentType: string,
  nodeData: any,
  validationContext: JE_JsonValidationContext
): string[] {
  const component = findNodeByType(componentType);
  if (component) {
    return component.getOutputClaims(
      nodeData,
      validationContext
    );
  } else {
    return [];
  }

}
export type ValidateClaimFn = (
  claimId: string,
  claimFieldName: string,
  mustBeSet: boolean,
  claimDescription?: string,
  defaultValue?: string,
  localNodeClaims?: Set<string>,
  localSAClaims?: Set<string>
) => void;

export function validateEditorNodeSettings(
  nodeData: any,
  validationContext: JE_JsonValidationContext,
  addErrorReport: (message: string) => void,
  validateClaim: ValidateClaimFn
) {
  const nodeType = nodeData.nodeData.componentType;
  const node = findNodeByType(nodeType);
  if (node) {
    node.validateJSON(nodeData, validationContext, addErrorReport, validateClaim);
    //ASSUME All nodes implemented to have one child
    if (nodeData.children.length !== 1) {
      addErrorReport(`Node expecting 1 child nodes, has ${nodeData.children.length} children`);
    }
  }
}

export { AdditionalNode };
